import { ConflictManager } from './managers/conflict-manager.js'
import { StateManager } from './managers/state-manager.js'
import { ModalManager } from './managers/modal-manager.js'
import { CalendarManager } from './managers/calendar-manager.js'
import './schedule-calendar.component.less'

/**
 * ScheduleCalendar Component
 * Integrates FullCalendar with AngularJS for schedule visualization
 */
class ScheduleCalendarController {
  constructor ($scope, $log, $timeout, $element, SceneCollectionService, CONFIGURATION) {
    'ngInject'

    this._$scope = $scope
    this._$log = $log
    this._$timeout = $timeout
    this._$element = $element
    this._SceneCollectionService = SceneCollectionService
    this._CONFIGURATION = CONFIGURATION

    this.calendar = null // Will be managed by CalendarManager, but keeping reference if needed or removing it?
    // Actually, CalendarManager manages this.calendar. The controller shouldn't need it directly unless for legacy reasons.
    // But wait, other managers might access this.calendar?
    // ConflictManager accesses this.calendar.
    // StateManager accesses this.calendar.
    // So we need to expose it or update other managers to access it via calendarManager.
    // For now, let's keep it simple: CalendarManager manages the instance.
    // We can expose a getter or just let managers access `this.controller.calendarManager.calendar`.

    // Instantiate Managers
    this.conflictManager = new ConflictManager(this)
    this.stateManager = new StateManager(this, SceneCollectionService)
    this.modalManager = new ModalManager(this)
    this.calendarManager = new CalendarManager(this, SceneCollectionService)
  }

  $onInit () {
    // Initialize Calendar Manager
    this.calendarManager.init()
    // Modal and State initialization handled by managers
  }

  $onChanges (changes) {
    // Handle scene ID changes
    if (changes.sceneId && changes.sceneId.currentValue) {
      this._$log.info('[ScheduleCalendar] Scene ID changed:', changes.sceneId.currentValue)
      // Re-initialize or update calendar if scene changes
      if (this.calendarManager) {
        this.calendarManager.forceInitialize()
      }
    }

    // Handle modal open state
    if (changes.isOpen) {
      if (this.isOpen) {
        this.openCalendarModal()
      } else {
        this.closeCalendarModal()
      }
    }
  }

  $onDestroy () {
    // Clean up modal (escape handler, nested modals) before destroying calendar
    this.modalManager.closeCalendarModal()
    this.calendarManager.destroy()
  }

  /**
   * Resolve overlap conflict
   * Delegate to ConflictManager
   */
  resolveOverlapConflict (conflict) {
    this.conflictManager.resolveOverlapConflict(conflict)
  }

  // State Manager Delegates
  createInterval (interval) {
    this.stateManager.createInterval(interval)
  }

  saveEventEdit (event) {
    this.stateManager.saveEventEdit(event)
  }

  deleteEvent (event) {
    this.stateManager.deleteEvent(event)
  }

  loadAvailableSceneCollections () {
    return this.stateManager.loadAvailableSceneCollections()
  }

  /**
   * Handle mousedown on the modal backdrop area.
   * Uses mousedown instead of click because during a FullCalendar drag,
   * the browser generates a click event on the common ancestor (.modal)
   * of the mousedown/mouseup targets — falsely triggering close.
   * With mousedown, target is always the element where the press started,
   * and stopPropagation on .modal-dialog prevents drag-initiated presses
   * from reaching this handler.
   */
  handleBackdropMouseDown ($event) {
    if ($event.target === $event.currentTarget) {
      this.closeCalendarModal()
    }
  }

  // Modal Manager Delegates
  openCalendarModal () {
    this.modalManager.openCalendarModal()
  }

  closeCalendarModal () {
    this.modalManager.closeCalendarModal()
  }

  openEventEditModal (event) {
    this.modalManager.openEventEditModal(event)
  }

  closeEventEditModal () {
    this.modalManager.closeEventEditModal()
  }

  showOverlapModal () {
    this.modalManager.showOverlapModal()
  }

  closeOverlapModal (revert) {
    this.modalManager.closeOverlapModal(revert)
  }

  openCreateIntervalModal (start, end) {
    this.modalManager.openCreateIntervalModal(start, end)
  }

  closeCreateIntervalModal () {
    this.modalManager.closeCreateIntervalModal()
  }

  // Calendar Manager Delegates
  updateIntervalTime (intervalId, newStart, newEnd, info, sceneCollectionId, priority) {
    return this.calendarManager.updateIntervalTime(intervalId, newStart, newEnd, info, sceneCollectionId, priority)
  }

  refreshAllEventStyles () {
    this.calendarManager.refreshAllEventStyles()
  }

  // State Manager property access (for ConflictManager)
  get pendingIntervalData () {
    return this.stateManager.pendingIntervalData
  }

  set pendingIntervalData (value) {
    this.stateManager.pendingIntervalData = value
  }

  proceedWithIntervalCreation (intervalData) {
    this.stateManager.proceedWithIntervalCreation(intervalData)
  }
}

export default {
  bindings: {
    sceneId: '<',
    isOpen: '<',
    onClose: '&'
  },
  controller: ScheduleCalendarController,
  template: require('./schedule-calendar.tpl.html').default
}
